import React, { useState, useEffect, useRef } from 'react';
import { accountingAPI, partyAPI, formatCurrency } from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, AlertCircle, CheckCircle, Paperclip } from 'lucide-react';
import { Button } from '../components/ui/button';
import PartyAutocomplete from '../components/PartyAutocomplete';

const VOUCHER_TYPES = [
  { value: 'sales', label: 'Sales Voucher', desc: 'Invoice / Revenue' },
  { value: 'purchase', label: 'Purchase Voucher', desc: 'Purchase / Expense' },
  { value: 'journal', label: 'Journal Voucher', desc: 'General adjustments' },
  { value: 'receipt', label: 'Receipt Voucher', desc: 'Money received' },
  { value: 'payment', label: 'Payment Voucher', desc: 'Money paid out' },
  { value: 'contra', label: 'Contra Voucher', desc: 'Bank/cash transfers' },
  { value: 'credit_note', label: 'Credit Note', desc: 'Sales return / discount' },
  { value: 'debit_note', label: 'Debit Note', desc: 'Purchase return' },
  { value: 'expense', label: 'Expense Voucher', desc: 'Record expenses' },
];

const emptyLine = { account_code: '', debit: '', credit: '', narration: '' };

export default function JournalEntryForm() {
  const navigate = useNavigate();
  const { entryId } = useParams();
  const isEdit = !!entryId;
  const [accounts, setAccounts] = useState([]);
  const [parties, setParties] = useState([]);
  const fileRef = useRef(null);
  const [attachment, setAttachment] = useState(null);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    voucher_type: 'journal',
    narration: '',
    party_name: '',
    lines: [{ ...emptyLine }, { ...emptyLine }]
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    accountingAPI.chartOfAccounts({}).then(r => {
      setAccounts(r.data.filter(a => !a.is_group));
    }).catch(() => {});
    partyAPI.list({}).then(r => {
      const data = Array.isArray(r.data) ? r.data : (r.data?.parties || []);
      setParties(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!entryId) return;
    setLoadingEntry(true);
    accountingAPI.getJournalEntry(entryId).then(r => {
      const e = r.data;
      setForm({
        date: e.date || '',
        voucher_type: e.voucher_type || 'journal',
        narration: e.narration || '',
        party_name: e.party_name || '',
        lines: (e.lines || []).map(l => ({
          account_code: l.account_code || '',
          debit: l.debit > 0 ? String(l.debit) : '',
          credit: l.credit > 0 ? String(l.credit) : '',
          narration: l.narration || '',
        })),
      });
    }).catch(() => {
      toast.error('Failed to load journal entry');
      navigate('/accounting/journal-entries');
    }).finally(() => setLoadingEntry(false));
  }, [entryId, navigate]);

  const updateLine = (idx, field, value) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      // Auto-populate the top-level party_name when a Party is selected as an account line
      let party_name = f.party_name;
      if (field === 'account_code' && typeof value === 'string' && value.startsWith('PARTY:')) {
        const selectedName = value.slice(6);
        if (!party_name || party_name.trim() === '') {
          party_name = selectedName;
        }
      }
      return { ...f, lines, party_name };
    });
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));

  const removeLine = (idx) => {
    if (form.lines.length <= 2) { toast.error('Minimum 2 lines required'); return; }
    setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };

  const totalDebit = form.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;
  const diff = Math.abs(totalDebit - totalCredit);

  const handleSubmit = async () => {
    if (!form.date) { toast.error('Select a date'); return; }
    if (!isBalanced) { toast.error('Entry must be balanced (Debit = Credit)'); return; }
    const hasEmpty = form.lines.some(l => !l.account_code || (!parseFloat(l.debit) && !parseFloat(l.credit)));
    if (hasEmpty) { toast.error('All lines must have an account and amount'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lines: form.lines.map(l => ({
          account_code: l.account_code,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          narration: l.narration
        }))
      };
      if (isEdit) {
        const res = await accountingAPI.editJournalEntry(entryId, payload);
        toast.success(`Voucher ${res.data.voucher_number} updated`);
      } else {
        const res = await accountingAPI.createJournalEntry(payload);
        if (attachment && res.data.entry_id) {
          try {
            await accountingAPI.uploadAttachment(res.data.entry_id, attachment);
          } catch { toast.error('Voucher created but attachment upload failed'); }
        }
        toast.success(`Voucher ${res.data.voucher_number} created`);
      }
      navigate('/accounting/journal-entries');
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} entry`); }
    finally { setSaving(false); }
  };

  const accountsByType = {};
  accounts.forEach(a => {
    const type = a.account_type;
    if (!accountsByType[type]) accountsByType[type] = [];
    accountsByType[type].push(a);
  });

  const typeLabels = {
    asset: 'Assets', liability: 'Liabilities', equity: 'Equity',
    revenue: 'Revenue', direct_expense: 'Direct Expenses', indirect_expense: 'Indirect Expenses'
  };

  return (
    <div data-testid="journal-entry-form" className="space-y-5 max-w-5xl">
      {loadingEntry ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : (<>
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">{isEdit ? 'Edit Voucher Entry' : 'New Voucher Entry'}</h1>
        <p className="text-sm text-slate-500 mt-1">{isEdit ? 'Modify this double-entry accounting transaction' : 'Create a double-entry accounting transaction'}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Date</label>
            <input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
              className="form-input" data-testid="je-date" />
          </div>
          <div>
            <label className="form-label">Voucher Type</label>
            <select value={form.voucher_type} onChange={(e) => setForm(f => ({ ...f, voucher_type: e.target.value }))}
              className="form-input" data-testid="je-voucher-type">
              {VOUCHER_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Party Name (optional)</label>
            <PartyAutocomplete
              value={form.party_name}
              onChange={(v) => setForm(f => ({ ...f, party_name: v }))}
              onPartySelect={(p) => setForm(f => ({ ...f, party_name: p.name }))}
              placeholder="Search party..."
              allowCreate={false}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Narration</label>
            <textarea value={form.narration} onChange={(e) => setForm(f => ({ ...f, narration: e.target.value }))}
              className="form-input min-h-[50px] resize-y" placeholder="Describe this transaction..." data-testid="je-narration" />
          </div>
          <div>
            <label className="form-label">Attachment (optional)</label>
            <div className="flex items-center gap-2">
              <input type="file" ref={fileRef} className="hidden" onChange={(e) => setAttachment(e.target.files[0])} data-testid="je-file-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="text-xs">
                <Paperclip className="w-3.5 h-3.5 mr-1" /> {attachment ? attachment.name : 'Upload File'}
              </Button>
              {attachment && <button onClick={() => setAttachment(null)} className="text-xs text-rose-500">Remove</button>}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">{VOUCHER_TYPES.find(v => v.value === form.voucher_type)?.desc}</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-800 text-sm">Transaction Lines</h3>
          <Button variant="outline" size="sm" onClick={addLine} className="text-xs" data-testid="je-add-line">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-3 py-2 text-left w-8">#</th>
                <th className="px-3 py-2 text-left">Account</th>
                <th className="px-3 py-2 text-right w-36">Debit (Rs.)</th>
                <th className="px-3 py-2 text-right w-36">Credit (Rs.)</th>
                <th className="px-3 py-2 text-left w-48">Narration</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((line, idx) => (
                <tr key={idx} className="border-b border-slate-100" data-testid={`je-line-${idx}`}>
                  <td className="px-3 py-2 text-xs text-slate-400">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <select value={line.account_code} onChange={(e) => updateLine(idx, 'account_code', e.target.value)}
                      className="form-input text-sm" data-testid={`je-line-account-${idx}`}>
                      <option value="">Select Account</option>
                      {parties.length > 0 && (
                        <optgroup label="Parties (Sundry Debtors / Creditors)">
                          {parties.map(p => <option key={`party-${p.party_id}`} value={p.gstin ? `PARTY:${p.name}` : `PARTY:${p.name}`}>
                            {p.name} {p.type === 'customer' ? '(Dr)' : p.type === 'vendor' ? '(Cr)' : ''} {p.gstin ? `| ${p.gstin}` : ''}
                          </option>)}
                        </optgroup>
                      )}
                      {Object.entries(accountsByType).map(([type, accs]) => (
                        <optgroup key={type} label={typeLabels[type] || type}>
                          {accs.map(a => <option key={a.account_code} value={a.account_code}>{a.account_code} - {a.account_name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" value={line.debit} onChange={(e) => updateLine(idx, 'debit', e.target.value)}
                      className="form-input font-mono text-right text-sm" placeholder="0.00" min="0" data-testid={`je-line-debit-${idx}`} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" value={line.credit} onChange={(e) => updateLine(idx, 'credit', e.target.value)}
                      className="form-input font-mono text-right text-sm" placeholder="0.00" min="0" data-testid={`je-line-credit-${idx}`} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="text" value={line.narration} onChange={(e) => updateLine(idx, 'narration', e.target.value)}
                      className="form-input text-sm" placeholder="Line note..." data-testid={`je-line-narration-${idx}`} />
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => removeLine(idx)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`je-line-remove-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-semibold">
                <td className="px-3 py-3" colSpan={2}>
                  <div className="flex items-center gap-2">
                    {isBalanced ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle className="w-4 h-4" /> Balanced</span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="w-4 h-4" /> Difference: Rs. {formatCurrency(diff)}</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-right font-mono text-sm" data-testid="je-total-debit">Rs. {formatCurrency(totalDebit)}</td>
                <td className="px-3 py-3 text-right font-mono text-sm" data-testid="je-total-credit">Rs. {formatCurrency(totalCredit)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/accounting/journal-entries')} data-testid="je-cancel">Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving || !isBalanced}
          className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="je-submit">
          {saving ? 'Saving...' : isEdit ? 'Update Voucher' : 'Post Voucher'}
        </Button>
      </div>
      </>)}
    </div>
  );
}

